import { create } from 'zustand';
import { api } from '@/services/api';

export type ModelProvider = 'claude' | 'gemini' | 'cerebras' | 'stark_protocol';

interface SettingsState {
  modelPreference: ModelProvider;
  modelOverride: string | null;  // specific model within provider (e.g., "claude-opus-4-6")
  voiceEnabled: boolean;
  use24HourTime: boolean;
  costCapAlertsEnabled: boolean;  // off by default; iMessage on daily API spend cap
  isLoading: boolean;

  setModelPreference: (provider: ModelProvider) => void;
  setModelOverride: (model: string | null) => void;
  setVoiceEnabled: (enabled: boolean) => void;
  setUse24HourTime: (enabled: boolean) => void;
  setCostCapAlertsEnabled: (enabled: boolean) => void;
  loadPreferences: () => Promise<void>;
  savePreferences: () => Promise<void>;
}

const STORAGE_KEY = 'jarvis-model-preference';
const VOICE_STORAGE_KEY = 'jarvis-voice-enabled';
const TIME_FORMAT_KEY = 'jarvis-24h-time';
const COST_CAP_ALERTS_KEY = 'jarvis-cost-cap-alerts';

function getStoredPreference(): ModelProvider {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && ['claude', 'gemini', 'cerebras', 'stark_protocol'].includes(stored)) {
      return stored as ModelProvider;
    }
  } catch {
    // ignore
  }
  return 'cerebras';
}

export const useSettingsStore = create<SettingsState>((set, get) => ({
  modelPreference: getStoredPreference(),
  modelOverride: localStorage.getItem('jarvis-model-override') || null,
  voiceEnabled: localStorage.getItem(VOICE_STORAGE_KEY) === 'true',
  use24HourTime: localStorage.getItem(TIME_FORMAT_KEY) === 'true',
  costCapAlertsEnabled: localStorage.getItem(COST_CAP_ALERTS_KEY) === 'true',
  isLoading: false,

  setModelOverride: (model: string | null) => {
    if (model) {
      localStorage.setItem('jarvis-model-override', model);
    } else {
      localStorage.removeItem('jarvis-model-override');
    }
    set({ modelOverride: model });
  },

  setUse24HourTime: (enabled: boolean) => {
    localStorage.setItem(TIME_FORMAT_KEY, String(enabled));
    set({ use24HourTime: enabled });
  },

  setVoiceEnabled: (enabled: boolean) => {
    localStorage.setItem(VOICE_STORAGE_KEY, String(enabled));
    set({ voiceEnabled: enabled });
    get().savePreferences();
  },

  setCostCapAlertsEnabled: (enabled: boolean) => {
    localStorage.setItem(COST_CAP_ALERTS_KEY, String(enabled));
    set({ costCapAlertsEnabled: enabled });
    get().savePreferences();
  },

  setModelPreference: (provider: ModelProvider) => {
    localStorage.setItem(STORAGE_KEY, provider);
    set({ modelPreference: provider });
    // Fire-and-forget save to server
    get().savePreferences();
  },

  loadPreferences: async () => {
    set({ isLoading: true });
    try {
      const prefs = await api.get<{ model_preference?: string; cost_cap_alerts_enabled?: boolean }>(
        '/auth/me/preferences',
      );
      if (prefs.model_preference) {
        const provider = prefs.model_preference as ModelProvider;
        localStorage.setItem(STORAGE_KEY, provider);
        set({ modelPreference: provider });
      }
      // Server is the source of truth for cost-cap alerts (the backend gates on
      // it); default off when the key is absent.
      const costCap = prefs.cost_cap_alerts_enabled === true;
      localStorage.setItem(COST_CAP_ALERTS_KEY, String(costCap));
      set({ costCapAlertsEnabled: costCap });
    } catch {
      // Use local storage fallback
    } finally {
      set({ isLoading: false });
    }
  },

  savePreferences: async () => {
    const { modelPreference, voiceEnabled, costCapAlertsEnabled } = get();
    try {
      await api.put('/auth/me/preferences', {
        model_preference: modelPreference,
        voice_enabled: voiceEnabled,
        cost_cap_alerts_enabled: costCapAlertsEnabled,
      });
    } catch {
      // Non-critical — local storage is the primary store
    }
  },
}));
